import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import CandidatesResumeUploadModal from './CandidatesResumeUploadModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/** 手動作成・CSVの2枚は固定リンク */
const STATIC_LINKS = [
  {
    title: '手動で登録',
    description: '空のフォームに手動で\n入力して登録します。',
    link: '/agent/candidates/new',
    icon: 'manual',
  },
  {
    title: 'CSVで一括登録',
    description: 'CSVをアップロードして\n求職者を一括登録します。',
    link: '/agent/candidates/candidate_import_requests/new',
    icon: 'csv',
  },
] as const;

export default function CandidatesAddModal({ isOpen, onClose }: Props) {
  // 書類から作成モーダルの開閉状態
  const [showResumeModal, setShowResumeModal] = useState(false);

  /** 書類から作成カードをクリックしたとき: 登録モーダルを閉じ書類アップロードモーダルを開く */
  const handleResumeCardClick = () => {
    onClose();
    setShowResumeModal(true);
  };

  return (
    // 2つの Dialog は独立した兄弟として並べる（互いの開閉状態に依存しない）
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
      >
        <DialogContent className="h-[550px] w-[1050px] overflow-hidden rounded-[24px] px-[80px] py-[87px] shadow-[0px_0px_24px_0px_rgba(0,_0,_0,_0.20)]!">
          <img src="/agent/modal-decorate-bottom.svg" alt="" className="absolute bottom-0 left-0" />
          <img src="/agent/modal-decorate-top.svg" alt="" className="absolute top-0 right-0" />
          <button
            onClick={onClose}
            className="absolute top-[36px] right-[46px] z-10 rounded-full bg-white p-2 hover:opacity-60"
            aria-label="閉じる"
          >
            <MdClose size={24} color="#818B9A" />
          </button>
          {/* コンテンツ */}
          <div className="relative z-10">
            <div className="flex flex-col items-center justify-center gap-sm">
              <DialogTitle asChild>
                <p className="text-3xl! font-bold!">求職者を登録</p>
              </DialogTitle>
              <p className="text-base">求職者を登録します。登録方法を選択してください。</p>
            </div>
            <div className="mt-x3l flex gap-6">
              {/* 手動作成・CSV取り込み */}
              {STATIC_LINKS.map((l, key) => {
                return (
                  <Link
                    to={l.link}
                    className="flex h-[250px] flex-1 flex-col items-center justify-center gap-md rounded-md bg-white p-md shadow-layer1"
                    key={key}
                  >
                    <img
                      src={`/agent/candidates/add-${l.icon}.svg`}
                      width={100}
                      height={100}
                      alt=""
                    />
                    <div className="flex flex-col items-center justify-center gap-2">
                      <p className="text-lg font-bold">{l.title}</p>
                      <p className="text-xs leading-[170%] whitespace-pre-wrap text-sub">
                        {l.description}
                      </p>
                    </div>
                  </Link>
                );
              })}
              {/* 書類から作成: リンクからボタンに変更してモーダルを開く */}
              <button
                type="button"
                onClick={handleResumeCardClick}
                className="flex h-[250px] flex-1 flex-col items-center justify-center gap-md rounded-md bg-white p-md shadow-layer1 hover:opacity-80"
              >
                <img src="/agent/candidates/add-resume.svg" width={100} height={100} alt="" />
                <div className="flex flex-col items-center justify-center gap-2">
                  <p className="text-lg font-bold">ファイルから登録</p>
                  <p className="text-xs leading-[170%] whitespace-pre-wrap text-sub">
                    {'履歴書・職歴書(PDF/Word)から\n求職者情報を自動生成します。'}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CandidatesResumeUploadModal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
      />
    </>
  );
}
